import { joinHtml, splitHtml } from "../core/document";
import DOMPurify from "dompurify";
import { SAMPLE_HTML } from "../data/sample";
import { createToolbar, refreshToolbar } from "./toolbar";

import { SourceEditor } from "./SourceEditor";

type EditorMode = "visual" | "source";

export class EditorPane {
  readonly el: HTMLElement;
  private source: SourceEditor;
  private canvas: HTMLElement;
  private fileInput: HTMLInputElement;
  private drop: HTMLElement;
  private formatBar: HTMLElement;
  private onChange: (html: string) => void;
  private html = "";
  private mode: EditorMode = "visual";
  private syncing = false;

  private onSelection: () => void = () => {};
  private onFile: (name: string) => void;
  private disposed = false;

  constructor(onChange: (html: string) => void, initial = "", mode: EditorMode = "visual", onFile: (name: string) => void = () => {}) {
    this.onFile = onFile;
    this.onChange = onChange;
    this.el = document.createElement("section");
    this.el.className = "hdp-editor";
    this.el.innerHTML = `
      <div class="hdp-pane-heading"><span class="hdp-eyebrow">01 / CREATE</span><h2>Your document</h2><p>A little code. Every screen.</p></div>
      <div class="hdp-editor__bar">
        <div class="hdp-tabs hdp-tabs--editor" role="tablist" aria-label="Editor mode">
          <button type="button" class="is-on" data-editor-mode="visual" role="tab" aria-selected="true">Visual</button>
          <button type="button" data-editor-mode="source" role="tab" aria-selected="false">Source</button>
        </div>
        <button type="button" class="hdp-btn" data-find title="Find and replace">Find</button>
        <button type="button" class="hdp-btn" data-upload title="Upload HTML">Open file</button>
      </div>
      <div class="hdp-editor__tools"></div>
      <div class="hdp-editor__stage">
        <div class="hdp-visual" contenteditable="true" role="textbox" aria-label="Visual HTML editor" data-placeholder="Type here, or upload an HTML file."></div>

        <div class="hdp-drop" hidden>
          <strong>Drop HTML file</strong>
          <span>Release to load the document</span>
        </div>
      </div>
      <div class="hdp-editor__foot"><span data-editor-info>HTML document</span><span>UTF-8</span></div>
      <input type="file" accept=".html,.htm,text/html" hidden>
    `;

    this.source = new SourceEditor((html) => {
      if (this.syncing) return;
      this.html = html;
      this.onChange(html);
    });
    this.el.querySelector(".hdp-editor__stage")!.prepend(this.source.el);
    this.canvas = this.el.querySelector(".hdp-visual")!;
    this.el.querySelector("[data-upload]")!.addEventListener("click", () => this.openUpload());
    this.el.querySelector("[data-find]")!.addEventListener("click", () => { this.setMode("source"); this.source.search(); });
    this.fileInput = this.el.querySelector('input[type="file"]')!;
    this.drop = this.el.querySelector(".hdp-drop")!;

    const emit = () => this.emitFromVisual();
    this.formatBar = createToolbar(this.canvas, () => emit());
    this.el.querySelector(".hdp-editor__tools")!.append(this.formatBar);

    this.setValue(initial);
    this.setMode(mode);

    this.canvas.addEventListener("input", emit);
    this.canvas.addEventListener("paste", (event) => {
      event.preventDefault();
      const html = event.clipboardData?.getData("text/html");
      if (html) document.execCommand("insertHTML", false, this.safeBody(html));
      else document.execCommand("insertText", false, event.clipboardData?.getData("text/plain") ?? "");
      emit();
    });
    this.canvas.addEventListener("keyup", () => refreshToolbar(this.formatBar));
    this.canvas.addEventListener("mouseup", () => refreshToolbar(this.formatBar));

    this.onSelection = () => {
      if (this.mode !== "visual") return;
      if (!this.selectionInCanvas()) return;
      refreshToolbar(this.formatBar);
    };
    document.addEventListener("selectionchange", this.onSelection);



    this.el.querySelectorAll<HTMLButtonElement>("[data-editor-mode]").forEach((btn) => {
      btn.addEventListener("click", () => this.setMode(btn.dataset.editorMode as EditorMode));
    });

    this.fileInput.addEventListener("change", () => {
      const file = this.fileInput.files?.[0];
      this.fileInput.value = "";
      if (file) void this.readFile(file);
    });

    const stage = this.el.querySelector(".hdp-editor__stage")!;
    stage.addEventListener("dragenter", (e) => this.onDrag(e, true));
    stage.addEventListener("dragover", (e) => this.onDrag(e, true));
    stage.addEventListener("dragleave", (e) => {
      if (!stage.contains((e as DragEvent).relatedTarget as Node)) {
        this.drop.hidden = true;
      }
    });
    stage.addEventListener("drop", (e) => {
      this.onDrag(e, false);
      const file = (e as DragEvent).dataTransfer?.files?.[0];
      if (file) void this.readFile(file);
    });
  }

  setValue(html: string): void {
    this.html = html;
    this.syncing = true;
    this.source.setValue(html);
    const body = this.safeBody(html);
    if (this.canvas.innerHTML !== body) this.canvas.innerHTML = body;
    this.syncing = false;
    this.refreshEmpty();
  }

  private safeBody(html: string): string {
    return DOMPurify.sanitize(splitHtml(html).body, { USE_PROFILES: { html: true }, FORBID_TAGS: ["style", "link", "form", "input", "button", "textarea", "select"], FORBID_ATTR: ["style", "id", "name"] });
  }

  destroy(): void {
    this.disposed = true;
    this.source.destroy();
    document.removeEventListener("selectionchange", this.onSelection);
  }

  attachRoot(): void { this.source.attachRoot(); }

  getValue(): string {
    return this.html;
  }

  loadSample(html = SAMPLE_HTML, filename = "harbor-rye.html"): void {
    this.setMode("source");
    this.onFile(filename);
    this.setValue(html);
    this.onChange(html);
  }

  newDocument(): void {
    this.setValue("");
    this.setMode("visual");
    this.onChange("");
    this.canvas.focus();
  }

  openUpload(): void {
    this.fileInput.click();
  }

  clear(): void {
    this.setValue("");
    this.onChange("");
  }

  private setMode(mode: EditorMode): void {
    this.mode = mode;
    this.el.querySelector("[data-editor-info]")!.textContent = mode === "source" ? "HTML · CSS · JavaScript" : "Basic formatting · use Source for full HTML";
    const visual = mode === "visual";
    this.canvas.hidden = !visual;
    this.source.el.hidden = visual;
    this.formatBar.hidden = !visual;
    this.el.classList.toggle("is-source", !visual);
    this.el.querySelectorAll<HTMLButtonElement>("[data-editor-mode]").forEach((btn) => {
      const on = btn.dataset.editorMode === mode;
      btn.classList.toggle("is-on", on);
      btn.setAttribute("aria-selected", String(on));
    });
    if (visual) {
      this.syncing = true;
      this.canvas.innerHTML = this.safeBody(this.html);
      this.syncing = false;
      this.refreshEmpty();
    } else {
      this.source.setValue(this.html);
    }
  }

  private emitFromVisual(): void {
    if (this.syncing) return;
    const body = this.canvas.innerHTML === "<br>" ? "" : this.canvas.innerHTML;
    this.html = this.html.trim() ? joinHtml(this.html, body) : body;
    this.source.setValue(this.html, true);
    this.refreshEmpty();
    this.onChange(this.html);
  }

  private refreshEmpty(): void {
    this.canvas.classList.toggle("is-empty", !this.canvas.textContent?.trim());
  }

  private selectionInCanvas(): boolean {
    const root = this.el.getRootNode();
    const selection =
      root instanceof ShadowRoot && "getSelection" in root
        ? (root as ShadowRoot & { getSelection(): Selection | null }).getSelection()
        : document.getSelection();
    if (!selection || selection.rangeCount === 0) return false;
    const node = selection.anchorNode;
    return Boolean(node && this.canvas.contains(node));
  }

  private onDrag(event: Event, show: boolean): void {
    event.preventDefault();
    event.stopPropagation();
    this.drop.hidden = !show;
  }

  private async readFile(file: File): Promise<void> {
    const name = file.name.toLowerCase();
    const ok =
      file.type === "text/html" ||
      name.endsWith(".html") ||
      name.endsWith(".htm") ||
      file.type === "text/plain";
    if (!ok) return;
    const text = await file.text();
    if (this.disposed) return;
    this.onFile(file.name);
    this.setMode("source");
    this.setValue(text);
    this.onChange(text);
  }
}
